/* eslint max-lines: ["error", { max: 348 }] -- this overrides for the entire file */
import { differenceInCalendarDays, format } from "date-fns";
import isNil from "lodash/isNil";

import { DateFormat } from "@/constants/DateFormat";
import { RecurrenceValue } from "@/constants/Schedule";

const MillisecondsPerSecond = 1000;
const SecondsPerMinute = 60;
const MinutesPerHour = 60;
const HoursPerDay = 24;
const DaysPerWeek = 7;

const toMs = ({
  days = 0,
  hours = 0,
  minutes = 0,
  seconds = 0,
}: {
  days?: number;
  hours?: number;
  minutes?: number;
  seconds?: number;
}) => {
  // eslint-disable-next-line unicorn/consistent-function-scoping,@typescript-eslint/no-shadow -- keeping this function-internal to prevent accidental reuse instead of toMs
  const fromSeconds = (seconds: number) => seconds * MillisecondsPerSecond;
  // eslint-disable-next-line @typescript-eslint/no-shadow
  const fromMinutes = (minutes: number) =>
    fromSeconds(minutes * SecondsPerMinute);
  // eslint-disable-next-line @typescript-eslint/no-shadow
  const fromHours = (hours: number) => fromMinutes(hours * MinutesPerHour);
  // eslint-disable-next-line @typescript-eslint/no-shadow
  const fromDays = (days: number) => fromHours(days * HoursPerDay);
  return (
    fromDays(days) +
    fromHours(hours) +
    fromMinutes(minutes) +
    fromSeconds(seconds)
  );
};

const getMsInSeconds = (milliseconds: number) =>
  milliseconds / MillisecondsPerSecond;

const getWeekday = (when: string): string => {
  return new Date(when).toLocaleString("en", {
    weekday: "long",
  });
};

const toIsoTimestamp = (date?: Date): string => {
  return isNil(date) ? "" : date.toISOString();
};

/**
 * Formats the event time range into a readable string.
 *
 * @param startDate - The start date and time of the event (string)
 * @param endDate - The end date and time of the event (string)
 * @returns A formatted string representing the event time range
 *
 */
const formatTime = ({
  date,
  shouldIncludePeriod = true,
}: {
  date: Date;
  shouldIncludePeriod?: boolean;
}): string => {
  const hour = format(date, "h");
  const minute = format(date, "mm");
  const timePeriod = format(date, "a").toLowerCase();
  const formattedMinute = minute === "00" ? "" : `:${minute}`;
  const formattedTimePeriod = shouldIncludePeriod ? timePeriod : "";
  return [hour, formattedMinute, formattedTimePeriod].join("");
};

/**
 * Formats the event time range into a readable string.
 *
 * @param startDate - The start date and time of the event (string)
 * @param endDate - The end date and time of the event (string)
 * @returns A formatted string representing the event time range
 *
 * @example
 * // Returns "1 - 2pm" for an event from 1:00 PM to 2:00 PM
 * // Returns "1:30 - 2:45pm" for an event from 1:30 PM to 2:45 PM
 * // Returns "11:30am - 12:30pm" for an event from 11:30 AM to 12:30 PM
 */
const getTimeRange = ({
  endDate,
  shouldIncludeStartAmPm = false,
  startDate,
}: {
  endDate?: string;
  shouldIncludeStartAmPm?: boolean;
  startDate: string;
}): string => {
  if (!endDate) {
    return formatTime({
      date: new Date(startDate),
      shouldIncludePeriod: shouldIncludeStartAmPm,
    });
  }

  const start = new Date(startDate);
  const end = new Date(endDate);

  const startAmPm = format(start, "a").toLowerCase();
  const endAmPm = format(end, "a").toLowerCase();

  const startTime = formatTime({
    date: start,
    shouldIncludePeriod: shouldIncludeStartAmPm || startAmPm !== endAmPm,
  });
  const endTime = formatTime({ date: end });

  return `${startTime} - ${endTime}`;
};

/**
 * Formats the event series date range into a readable string.
 *
 * @param startDate - The start date and time of the event (string)
 * @param endDate - The end date and time of the event (string)
 * @returns A formatted string representing the event date range
 *
 * @example
 * // Returns "Jul 1 - Dec 31 2024"
 * // Returns "Mar 5 2024 - Apr 3 2025"
 */
const getDateRange = ({
  endDate,
  startDate,
}: {
  endDate?: string;
  startDate: string;
}): string => {
  if (!endDate) return "";

  const start = new Date(startDate);
  const end = new Date(endDate);

  const isYearMismatch =
    format(start, DateFormat.year) !== format(end, DateFormat.year);
  const startFormat = isYearMismatch
    ? DateFormat.shortMonthDayYear
    : DateFormat.shortMonthDay;

  const startDisplay = format(start, startFormat);
  const endDisplay = format(end, DateFormat.shortMonthDayYear);
  return `${startDisplay} - ${endDisplay}`;
};

/**
 * Calculates the end time for a given start date and duration (in minutes)
 *
 * @param startDate - The start date and time of the event (string)
 * @param durationMinutes - The duration of the event in minutes (number)
 * @returns A Date object representing the end date and time
 */
const getEndTime = ({
  durationMinutes,
  startDate,
}: {
  durationMinutes?: number;
  startDate: string;
}): Date | null => {
  if (isNil(durationMinutes) || durationMinutes <= 0) return null;
  const start = new Date(startDate);
  start.setMinutes(start.getMinutes() + durationMinutes);
  return start;
};

/**
 * Returns the next start date for a recurring event.
 *
 * @param startDate - The start date of the event (string)
 * @param frequency - The frequency of the event recurrence
 * @returns The next start date as a Date object, or undefined if the event does not recur
 */
const getNextStartDate = ({
  frequency,
  startDate,
}: {
  frequency: string;
  startDate: string;
}): string | undefined => {
  if (frequency === RecurrenceValue.DoesNotRepeat) {
    return undefined;
  }

  const start = new Date(startDate);
  switch (frequency) {
    case RecurrenceValue.Biweekly:
      start.setDate(start.getDate() + 14);
      break;
    case RecurrenceValue.Monthly:
      start.setMonth(start.getMonth() + 1);
      break;
    case RecurrenceValue.Weekly:
      start.setDate(start.getDate() + 7);
      break;
    default:
      return undefined;
  }

  return format(start, DateFormat.weekDayShortMonthDay);
};

/**
 * Determines if an event is starting within the next 15 minutes.
 * This function takes a start date for an event and checks if it's within
 * the next 15 minutes from the current time. It works as follows:
 *
 * @param startDate - The start date and time of the event (string or Date object)
 * @param endDate - The end date and time of the event (string or Date object)
 * @returns boolean - True if the event starts within the next 15 minutes, false otherwise
 */
const isEventWithin15Minutes = ({
  endDate,
  startDate,
}: {
  endDate: string;
  startDate: string;
}): boolean => {
  const eventStartTime = new Date(startDate);
  const eventEndTime = new Date(endDate);
  const currentTime = new Date();
  const timeToStart = eventStartTime.getTime() - currentTime.getTime();
  const fifteenMinutesInMilliseconds = toMs({ minutes: 15 });

  return (
    (timeToStart <= fifteenMinutesInMilliseconds && timeToStart > 0) ||
    (currentTime >= eventStartTime && currentTime < eventEndTime)
  );
};

const sortDateComparator = ({
  first,
  isDescending = false,
  second,
}: {
  first: string;
  isDescending?: boolean;
  second: string;
}): number => {
  const firstDate = new Date(first);
  const secondDate = new Date(second);
  const comparison = firstDate < secondDate ? -1 : 1;
  // defaults to ascending order
  return isDescending ? -1 * comparison : comparison;
};

const formatISODate = ({
  dateString,
  format: formatString,
}: {
  dateString: string;
  format: DateFormat;
}): string => {
  return format(new Date(dateString), formatString);
};

// TODO: Replace formatISODate with this function for all date formatting where date is a Date object instead of a string
const formatDate = ({
  date,
  format: formatString,
}: {
  date: Date;
  format: DateFormat;
}): string => {
  return format(date, formatString);
};

const combineDateTime = ({ date, time }: { date: Date; time: Date }) => {
  const combined = new Date(date);
  combined.setHours(time.getHours(), time.getMinutes());
  return combined;
};

const getRelativeDay = (later: Date | string) => {
  // if today is Monday January 1, for each date (jan 1, jan 2, jan 3, etc) show:
  //  - Today, Tomorrow, Wednesday, Thursday, Friday, Saturday, Sunday,
  //  - Next Monday, Next Tuesday, ..., Next Sunday,
  //  - Jan 15, Jan 16, Jan 17, ..
  const laterDate = new Date(later);
  const now = new Date();
  const daysAway = differenceInCalendarDays(laterDate, now);

  const weekDay = format(laterDate, DateFormat.weekDay);
  const farawayDate = format(laterDate, DateFormat.shortMonthDay);

  if (daysAway < -DaysPerWeek) {
    return farawayDate;
  }
  if (daysAway < -1) {
    return `Last ${weekDay}`;
  }
  if (daysAway === -1) {
    return "Yesterday";
  }
  if (daysAway === 0) {
    return "Today";
  }
  if (daysAway === 1) {
    return "Tomorrow";
  }
  if (daysAway < DaysPerWeek) {
    return weekDay;
  }

  return farawayDate;
};

const getGreetingFromDate = (date: Date): string => {
  const currentHour = date.getHours();

  if (currentHour < 4) {
    return "Good evening";
  }
  if (currentHour < 12) {
    return "Good morning";
  }
  if (currentHour < 17) {
    return "Good afternoon";
  }
  return "Good evening";
};

export {
  combineDateTime,
  formatDate,
  formatISODate,
  formatTime,
  getDateRange,
  getEndTime,
  getGreetingFromDate,
  getMsInSeconds,
  getNextStartDate,
  getRelativeDay,
  getTimeRange,
  getWeekday,
  isEventWithin15Minutes,
  sortDateComparator,
  toIsoTimestamp,
  toMs,
};
